import dayjs from "dayjs";
import {
  agGridDateFormatter,
  formatCurrency,
} from "../../services/valueFormatters.js";
import { patchShipment } from "../../api/imports";
import { IconButton, Chip, Stack, Menu, MenuItem, Box } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";
import { getLandTransitDays } from "./ShipmentCrudColumns.js";

const ActionsCell = (params) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget); 
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={0.5}>
      {/* <IconButton
        size="small"
        onClick={() => params.context.onDownload(params.data.id)}
        sx={{ color: "primary.main" }}
      >
        <DownloadIcon fontSize="small" />
      </IconButton> */}
      <IconButton size="small" onClick={handleClick} color="primary">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "more-button",
        }}
      >
        <MenuItem
          onClick={() => {
            params.context.onDelete(params.data.id);
            handleClose();
          }}
        >
          Eliminar
        </MenuItem>
        <MenuItem
          onClick={() => {
            params.context.onViewDocuments?.(params.data);
            handleClose();
          }}
        >
          Documentos
        </MenuItem>
        <MenuItem
          onClick={() => {
            params.context.onViewContainers?.(params.data);
            handleClose();
          }}
        >
          Contenedores
        </MenuItem>
        <MenuItem
          onClick={() => {
            params.context.onViewPurchaseOrders?.(params.data);
            handleClose();
          }}
        >
          Órdenes de Compra
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const currencyFormatterUSD = {
  valueFormatter: (params) =>
    params.value != undefined
      ? formatCurrency(params.value, "USD")
      : params.value,
};

const setStatusColor = (statusCode) => {
  switch (statusCode) {
    case "EMBARCADO":
      return "default";
    case "EN_PUERTO":
      return "warning";
    case "EN_TRANSITO_LOCAL":
      return "info";
    case "ENTREGADO":
      return "success";
    default:
      return "default";
  }
};

export const createShipmentColumns = (catalogues, statuses) => [
  {
    field: "actions",
    headerName: "Acciones",
    pinned: "right",
    sortable: false,
    filter: false,
    cellRenderer: ActionsCell,
  },
  {
    field: "provider.code",
    headerName: "Proveedor",
    sortable: true,
    filter: true,
  },
  {
    field: "ref",
    headerName: "Ref",
    sortable: true,
    filter: true,
    editable: true,
    pinned: "left",
  },
  {
    field: "statusId",
    headerName: "Estado",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Chip
          size="small"
          label={params.data.status?.name || ""}
          color={setStatusColor(params.data.status?.code)}
        />
      </Box>
    ),
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: statuses || [],
      valueField: "id",
      displayField: "name",
      updateFunction: patchShipment,
    },
  },
  {
    field: "remainingDays",
    headerName: "Dias restantes",
    sortable: true,
    filter: true,
    cellRenderer: (params) => {
      const remainingDays = -1 * dayjs().diff(params.data.portArrivalDate, "d");
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            size="small"
            label={
              params.data.status.code === "ENTREGADO" ? "-" : remainingDays
            }
            color={
              params.data.status.code === "ENTREGADO"
                ? "default"
                : remainingDays > 5
                ? "success"
                : remainingDays > 0
                ? "warning"
                : "error"
            }
          />
        </Box>
      );
    },
  },
  {
    field: "tariffFraction.name",
    headerName: "Material",
    sortable: true,
    filter: true,
  },
  {
    field: "isFsc",
    headerName: "FSC",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "tariffFractionId",
    headerName: "Fracción Arancelaria",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.tariffFraction?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["TARIFF_FRACTION"] || [],
      valueField: "id",
      displayField: ["code", "name"],
      displaySeparator: " | ",
      updateFunction: patchShipment,
      onError: (error) => {
        console.error("Error updating tariff fraction:", error);
      },
    },
  },
  {
    field: "igi",
    headerName: "IGI",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: (params) => {
      if (params.value != null) {
        return `${params.value.toFixed(2)}%`;
      }
      return "";
    },
  },
  {
    field: "totalNetWeight",
    headerName: "Peso Neto del material (kg)",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "cbm",
    headerName: "CBM",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "requestDate",
    headerName: "Fecha de pedido",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: agGridDateFormatter,
  },
  {
    field: "deliveryDate",
    headerName: "Fecha de Entrega",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: agGridDateFormatter,
  },
  {
    field: "departureDate",
    headerName: "Fecha de Zarpe",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: agGridDateFormatter,
  },
  {
    field: "shipmentTransportTypeId",
    headerName: "Tipo de Envío",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.shipmentTransportType?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["SHIPMENT_TRANSPORT_TYPE"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "portDestinationId",
    headerName: "Puerto de llegada",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.portDestination?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["PORT_DESTINATION"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "foreignTransportId",
    headerName: "Transporte Extranjero",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.foreignTransport?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["FOREIGN_TRANSPORT"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "masterBillofLanding",
    headerName: "Master Bill of Landing",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "hasForwarder",
    headerName: "Tiene Forwarder",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "forwarderId",
    headerName: "Forwarder",
    sortable: true,
    filter: true,
    editable: (params) => params.data.hasForwarder,
    cellStyle: (params) => {
      if (!params.data.hasForwarder) {
        return { backgroundColor: "#888", color: "#999" };
      }
      return null;
    },
    cellRenderer: (params) => params.data.forwarder?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["FORWARDER"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "forwarderGuideNo",
    headerName: "No. Guía Forwarder",
    sortable: true,
    filter: true,
    editable: (params) => params.data.hasForwarder,
    cellStyle: (params) => {
      if (!params.data.hasForwarder) {
        return { backgroundColor: "#888", color: "#999" };
      }
      return null;
    },
  },
  {
    field: "portOfOriginId",
    headerName: "Puerto de Origen",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.portOfOrigin?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["PORT_OF_ORIGIN"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "oceanFreightCost",
    headerName: "Costo Flete Marítimo (USD)",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: (params) => {
      if (params.value != null) {
        return formatCurrency(params.value, "MXN");
      }
      return "";
    },
  },
  {
    field: "numContainers",
    headerName: "Número de Contenedores",
    sortable: true,
    filter: true,
  },
  {
    field: "containerCol",
    headerName: "Contenedores",
    sortable: false,
    filter: false,
    cellRenderer: (params) => {
      if (!params.data.containers || params.data.containers.length === 0)
        return "";
      return (
        <Stack direction="column" spacing={0.5} key={params.data.id}>
          {params.data.containers.map((container) => (
            <Chip
              size="small"
              label={container.code}
              className="chip"
              key={container.id}
            />
          ))}
        </Stack>
      );
    },
    autoHeight: true,
  },
  {
    field: "customsAgentId",
    headerName: "Agente Aduanal",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.customsAgent?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["CUSTOMS_AGENT"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "patent",
    headerName: "Patente",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "customsAgentCost",
    headerName: "Costo Agente Aduanal",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: (params) => {
      if (params.value != null) {
        return formatCurrency(params.value, "MXN");
      }
      return "";
    },
  },
  {
    field: "requestNo",
    headerName: "No. Pedimento",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "invoiceNo",
    headerName: "No. Factura",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "shipmentTypeId",
    headerName: "Tipo de Embarque",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.shipmentType?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["SHIPMENT_TYPE"] || [],
      valueField: "id",
      displayField: "name",
      updateFunction: patchShipment,
    },
  },
  {
    field: "shipmentModeId",
    headerName: "Modo de Embarque",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.shipmentMode?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["SHIPMENT_MODE"] || [],
      valueField: "id",
      displayField: "name",
      updateFunction: patchShipment,
    },
  },
  {
    field: "forwarderLandId",
    headerName: "Forwarder Terrestre",
    sortable: true,
    filter: true,
    editable: true,
    cellRenderer: (params) => params.data.forwarderLand?.code || "",
    cellEditor: "UpdateDropdownEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      options: catalogues["FORWARDER_LAND"] || [],
      valueField: "id",
      displayField: "code",
      updateFunction: patchShipment,
    },
  },
  {
    field: "forwarderLandCost",
    headerName: "Costo Forwarder Terrestre",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: (params) => {
      if (params.value != null) {
        return formatCurrency(params.value, "MXN");
      }
      return "";
    },
  },
  {
    field: "landCostByContainer",
    headerName: "Costo Forwarder Terrestre por Contenedor",
    sortable: true,
    filter: true,
    valueFormatter: (params) => {
      if (params.value != null) {
        return formatCurrency(params.value, "MXN");
      }
      return "";
    },
  },
  {
    field: "freeDays",
    headerName: "Días Libres",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "portArrivalDate",
    headerName: "Fecha de Llegada al Puerto",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: agGridDateFormatter,
    pinned: "left",
  },
  {
    field: "plantArrivalDate",
    headerName: "Fecha de Llegada a Planta",
    sortable: true,
    filter: true,
    editable: true,
    valueFormatter: agGridDateFormatter,
  },
  {
    field: "landTransitDays",
    headerName: "Días de transito terrestre",
    sortable: true,
    filter: true,
    hide: true,
    valueGetter: (params) => getLandTransitDays(params.data),
  },
  {
    field: "originPlantTransitDays",
    headerName: "Días de Tránsito Origen a Planta",
    sortable: true,
    filter: true,
  },
  {
    field: "portPlantTransitDays",
    headerName: "Días de Tránsito Puerto a Planta",
    sortable: true,
    filter: true,
  },
  {
    field: "hasStorage",
    headerName: "Tiene Almacenaje",
    sortable: true,
    filter: true,
    editable: true,
  },
  {
    field: "storageCost",
    headerName: "Costo de Almacenaje",
    sortable: true,
    filter: true,
    editable: (params) => params.data.hasStorage,
    cellStyle: (params) => {
      if (!params.data.hasStorage) {
        return { backgroundColor: "#888", color: "#999" };
      }
      return null;
    },
    valueFormatter: (params) => {
      if (params.value != null) {
        return formatCurrency(params.value, "MXN");
      }
      return "";
    },
  },
  {
    field: "delayCharges",
    headerName: "Cargos por Demora",
    sortable: true,
    filter: true,
  },
  {
    field: "observations",
    headerName: "Observaciones",
    sortable: true,
    filter: true,
    editable: true,
  },
];
